import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, Home, KeyRound, LogOut, Settings, ShieldCheck } from 'lucide-react';
import { initials, useLogout, useMe } from '../lib/auth.ts';
import s from './shell.module.css';

interface UserMenuProps {
  /** up = sivupaneelin alalaidassa, down = yläpalkissa (puhelin) */
  placement: 'up' | 'down';
}

/** Käyttäjän nimi ja valikko: asetukset, näkymän vaihto, ylläpito ja uloskirjautuminen. */
export function UserMenu({ placement }: UserMenuProps) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const logout = useLogout();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!me) return null;
  const inTenantView = location.pathname.startsWith('/vuokralainen');
  const base = inTenantView ? '/vuokralainen' : '/vuokranantaja';
  const compact = placement === 'down';

  return (
    <div className={s.userMenu} ref={ref}>
      <button
        type="button"
        className={s.userButton}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('topbar.userMenu')}
        onClick={() => setOpen((v) => !v)}
        style={compact ? { padding: '0.2rem', width: 'auto' } : undefined}
      >
        <span className={s.avatar} aria-hidden="true">
          {initials(me)}
        </span>
        {!compact && (
          <>
            <span className={s.userText}>
              <span className={s.userName}>{me.name ?? me.email}</span>
              {me.name && <span className={s.userEmail}>{me.email}</span>}
            </span>
            <ChevronsUpDown size={16} className={s.userChevron} aria-hidden="true" />
          </>
        )}
      </button>

      {open && (
        <div className={`${s.menu} ${placement === 'up' ? s.menuUp : s.menuDown}`} role="menu">
          <Link to={`${base}/tili`} className={s.menuItem} role="menuitem">
            <Settings size={17} /> {t('nav.account')}
          </Link>
          {me.roles.landlord && me.roles.tenant && (
            <Link
              to={inTenantView ? '/vuokranantaja' : '/vuokralainen'}
              className={s.menuItem}
              role="menuitem"
            >
              {inTenantView ? <KeyRound size={17} /> : <Home size={17} />}
              {inTenantView ? t('nav.landlordView') : t('nav.tenantView')}
            </Link>
          )}
          {me.isAdmin && (
            <Link to="/admin" className={s.menuItem} role="menuitem">
              <ShieldCheck size={17} /> {t('nav.admin')}
            </Link>
          )}
          <div className={s.menuSep} />
          <button
            type="button"
            className={s.menuItem}
            role="menuitem"
            onClick={() => logout.mutate()}
          >
            <LogOut size={17} /> {t('common.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
